/* oxlint-disable react/no-danger */
import { ActionAfterViewObserver, TLifecycleHookEvent } from '@cian/react-utils';
import {
  ActionLink,
  Body1,
  Body2,
  Button,
  ITagOption,
  Skeleton,
  SkeletonGroup,
  Tags,
  UIHeading2,
  UIHeading3,
} from '@cian/ui-kit';
import * as React from 'react';

import styles from './EmptyStateBanner.css';
import lampImage from './assets/lamp.png';
import { EEmptyStateActionType, IEmptyState, IEmptyStateAction, IEmptyStateTag } from '../../types/emptyState';

/**
 * Все теги показываем одним «облаком» без сворачивания: в блоке пустой выдачи их немного,
 * а пользователю нужно сразу видеть весь набор фильтров, которые можно снять.
 */
const DISPLAY_MODE_OPTIONS = {
  isFolded: false,
  isFoldable: false,
};

/** Стабильная ссылка, чтобы не пересчитывать опции тегов на каждый рендер */
const EMPTY_TAGS: IEmptyStateTag[] = [];

/**
 * Яндекс читает `<!--noindex-->` как маркеры в исходном HTML, а React не умеет рендерить
 * комментарии, поэтому отдаём их через dangerouslySetInnerHTML. Так маркеры попадают в серверный
 * ответ: при добавлении комментариев на клиенте обходчик их не увидел бы.
 */
const NOINDEX_START = { __html: '<!--noindex-->' };
const NOINDEX_END = { __html: '<!--/noindex-->' };

/** Размеры шейпа тега из макета */
const TAG_SKELETON_HEIGHT = 28;
const TAG_SKELETON_BORDER_RADIUS = 12;
const TAG_SKELETON_WIDTH = 131;

interface IEmptyStateBannerProps {
  emptyState: IEmptyState;
  /** Выдача перезапрашивается — вместо тегов показываем скелетоны */
  isLoading: boolean;
  onShow(): void;
  onTagRemove(tag: IEmptyStateTag): void;
  onActionClick(action: IEmptyStateAction): void;
}

export const EmptyStateBanner: React.FC<IEmptyStateBannerProps> = ({
  emptyState,
  isLoading,
  onShow,
  onTagRemove,
  onActionClick,
}) => {
  const { title, subTitle, tags = EMPTY_TAGS } = emptyState;

  /**
   * `changeRegion` пока не поддерживаем: ручка присылает его только для поиска по области карты,
   * а на десктопной выдаче bbox появится после перехода на гибридку. До тех пор игнорируем
   * действие целиком, чтобы не показывать кнопку, которая ничего осмысленного не сделает.
   */
  const action = emptyState.action?.type === EEmptyStateActionType.ChangeRegion ? undefined : emptyState.action;

  const options = React.useMemo<ITagOption[]>(
    () =>
      tags.map(tag => ({
        label: tag.title,
        value: tag.id,
        sideView: <Body2 color="text-main-default">{tag.formattedCount}</Body2>,
      })),
    [tags],
  );

  const handleTagClose = React.useCallback(
    (option: ITagOption) => {
      const tag = tags.find(({ id }) => id === option.value);

      if (tag) {
        onTagRemove(tag);
      }
    },
    [onTagRemove, tags],
  );

  /** Показ считаем один раз: после первого входа во вьюпорт отписываемся от наблюдателя */
  const handleInViewport = React.useCallback(
    ({ subscribeState }: TLifecycleHookEvent) => {
      subscribeState.setSubscribeUnavailable();
      onShow();
    },
    [onShow],
  );

  const handleActionClick = React.useCallback(() => {
    if (action) {
      onActionClick(action);
    }
  }, [action, onActionClick]);

  /**
   * Когда снимать нечего — например, ручка не смогла собрать теги, — показываем компактный блок
   * без картинки: заголовок с подзаголовком и кнопку действия рядом с ними.
   */
  const content =
    options.length === 0 ? (
      <div className={styles['compact-banner']}>
        <div className={styles['text']}>
          <UIHeading2 as="h3">{title}</UIHeading2>
          {subTitle && (
            <Body1 as="div" color="text-secondary-default">
              {subTitle}
            </Body1>
          )}
        </div>
        {action && (
          <Button theme="main_secondary" size="M" onClick={handleActionClick} data-testid="EmptyStateBannerAction">
            {action.title}
          </Button>
        )}
      </div>
    ) : (
      <div className={styles['banner']}>
        <img className={styles['image']} src={lampImage} alt="" />
        <div className={styles['content']}>
          <div className={styles['text']}>
            <UIHeading3 as="h3">{title}</UIHeading3>
            {subTitle && (
              <Body2 as="div" color="text-secondary-default">
                {subTitle}
              </Body2>
            )}
          </div>
          {isLoading ? (
            <SkeletonGroup data-testid="EmptyStateBannerTagsSkeleton">
              {/* Раскладка на своём элементе: у корня SkeletonGroup собственный flex-direction */}
              <div className={styles['tags-skeleton']}>
                {options.map(({ value }) => (
                  <Skeleton
                    key={value}
                    width={TAG_SKELETON_WIDTH}
                    height={TAG_SKELETON_HEIGHT}
                    borderRadius={TAG_SKELETON_BORDER_RADIUS}
                  />
                ))}
              </div>
            </SkeletonGroup>
          ) : (
            <div className={styles['tags']}>
              <Tags
                options={options}
                onClose={handleTagClose}
                displayModeOptions={DISPLAY_MODE_OPTIONS}
                rightAdornment={
                  action && (
                    <div className={styles['action']}>
                      <ActionLink fontWeight="bold" onClick={handleActionClick} data-testid="EmptyStateBannerAction">
                        {action.title}
                      </ActionLink>
                    </div>
                  )
                }
              />
            </div>
          )}
        </div>
      </div>
    );

  return (
    <div data-testid="EmptyStateBanner">
      <span dangerouslySetInnerHTML={NOINDEX_START} />
      <ActionAfterViewObserver<HTMLDivElement> onInViewport={handleInViewport}>
        {({ elementRef }) => <div ref={elementRef}>{content}</div>}
      </ActionAfterViewObserver>
      <span dangerouslySetInnerHTML={NOINDEX_END} />
    </div>
  );
};
